import { useEffect, useRef } from "react";

// iOS home-screen apps can leave position:fixed bars floating after keyboard/viewport changes; re-anchor them.
// Never shift by the visual-viewport gap: mid keyboard-close it equals the keyboard height and strands the bar mid-screen.
export function usePinnedBottom() {
  const ref = useRef(null);
  useEffect(() => {
    const vv = window.visualViewport;
    const standalone = window.navigator.standalone === true || window.matchMedia?.("(display-mode: standalone)").matches;
    const typing = () => {
      const a = document.activeElement;
      return a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA" || a.isContentEditable);
    };
    const keyboardOpen = () => vv && window.innerHeight - vv.height > 120;
    const timers = [];
    const apply = () => {
      const el = ref.current;
      if (!el) return;
      let shift = 0;
      if (standalone && !typing() && !keyboardOpen()) {
        // innerHeight can stay short after the keyboard closes — push the bar back down to the real screen edge.
        const landscape = window.innerWidth > window.innerHeight;
        const full = landscape ? Math.min(window.screen.width, window.screen.height) : Math.max(window.screen.width, window.screen.height);
        const gap = full - window.innerHeight;
        if (gap > 0 && gap < 200) shift = gap;
      }
      el.style.transform = shift ? `translate3d(0, ${shift}px, 0)` : "";
    };
    // Re-check while the keyboard animation settles (iOS doesn't always fire a final resize).
    const fix = () => {
      timers.splice(0).forEach(clearTimeout);
      [0, 120, 400, 900].forEach((ms) => timers.push(setTimeout(apply, ms)));
    };
    fix();
    const evs = [[vv, "resize"], [vv, "scroll"], [window, "resize"], [window, "orientationchange"], [window, "pageshow"],
      [document, "focusin"], [document, "focusout"], [document, "visibilitychange"]];
    evs.forEach(([t, e]) => t?.addEventListener(e, fix));
    return () => {
      timers.forEach(clearTimeout);
      evs.forEach(([t, e]) => t?.removeEventListener(e, fix));
    };
  }, []);
  return ref;
}
